import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map, has } from 'lodash';
import Vue from 'vue';

import { set, unset } from '@/helpers';
import StockApi from '@/extensions/shop/store/api/stock';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

const defaultForm = {
    name: '',
    key: '',
    status: 'enabled',
    address: null,
    restriction: null,
    customerGroups: [],
    metas: []
};

const defaultRules = {
    name: 'required',
    key: 'required',
};

const defaultUi = {};

export const useShopStockStore = defineStore('shopStock', {
    state: () => ({
        loading: true,
        itemsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            status: {
                loading: false,
                items: [
                    { value: 'enabled', label: 'Enabled' },
                    { value: 'disabled', label: 'Disabled' }
                ]
            },
            restriction: {
                loading: false,
                items: [
                    { value: null, label: 'Anyone' },
                    { value: 'guests', label: 'Guests' },
                    { value: 'customer-groups', label: 'Customer Groups' }
                ]
            },
            customerGroup: {
                loading: false,
                items: []
            },
        },
        stocks: [],
        activeStock: undefined,
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        computedRules: (state) => {
            const rules = cloneDeep(state.rules);
            if (state.form.restriction === 'customer-groups') rules.customerGroups = 'required';
            return rules;
        }
    },

    actions: {
        setState({ path, value }) {
            set(this, path, value);
        },

        unsetState(path) {
            unset(this, path);
        },

        addItemsPaginatedToState(data) {
            this.itemsPaginated.items = data.items;
            this.itemsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.itemsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.itemsPaginated.link = cloneDeep(data.config.link);
            this.itemsPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.itemsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.itemsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.itemsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.itemsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.itemsPaginated.pageInfo.currentPage = currentPage;
        },

        defineSorting({ key, value }) {
            Vue.set(this.itemsPaginated.variables, 'order', key);
            Vue.set(this.itemsPaginated.variables, 'direction', value);
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.itemsPaginated.filters, key);
            } else {
                Vue.set(this.itemsPaginated.filters, key, value);
            }
        },

        definePathValue(value) {
            Vue.set(this.itemsPaginated, 'path', value);
        },

        defineFilterVisibility(value) {
            Vue.set(this.itemsPaginated, 'showFilters', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        insertStock(stock) {
            Vue.set(this, 'form', stock);
        },

        insertStocks(stocks) {
            Vue.set(this, 'stocks', stocks);
        },

        resetForm() {
            Vue.set(this, 'form', cloneDeep(defaultForm));
        },

        updateActiveStock(stockName) {
            let activeStock = find(this.stocks, stock => stock.name == stockName);
            Vue.set(this, 'activeStock', activeStock);
        },

        setInitialActiveStock(data) {
            Vue.set(this, 'activeStock', data[0]);
        },

        setUpsNr(value) {
            let upsMeta = { key: 'ups-account-nr', value };
            var copy = cloneDeep(this.form.metas);
            let objIndex = this.form.metas.findIndex(item => item.key == 'ups-account-nr');
            if (objIndex >= 0) {
                copy[objIndex] = upsMeta;
            } else {
                copy.push(upsMeta);
            }
            Vue.set(this.form, 'metas', copy);
        },

        updateCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.customerGroup, 'loading', value);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        // Async actions
        fetchPaginatedItems(shopID) {
            return StockApi.fetchStocksPaginated(this.itemsPaginated, shopID).then(data => {
                this.addItemsPaginatedToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchStock(stockId) {
            return StockApi.fetchStock(stockId).then(data => {
                this.insertStock(data);
            });
        },

        fetchStocks() {
            return StockApi.fetchStocks().then(data => {
                this.setInitialActiveStock(data);
                this.insertStocks(data);
            });
        },

        fetchCustomerGroupOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeCms.shop.defaultCustomerGroup'))
                this.insertCustomerGroupOptions([setupStore.activeCms.shop.defaultCustomerGroup]);

            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(shopID)
                .then(customerGroups => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch(err => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },

        create(stock) {
            const setupStore = useSetupStore(pinia);
            stock = cloneDeep(stock);

            const shop = setupStore.activeCms.shop;
            stock.shop = { id: shop.id };

            return StockApi.create(stock).then((stock) => {
                this.insertStock(cloneDeep(stock));
                return stock;
            });
        },

        update(stock) {
            return StockApi.update(stock).then((stock) => {
                this.insertStock(cloneDeep(stock));
                return stock;
            });
        },

        delete(id) {
            return StockApi.delete(parseInt(id)).then(() => {
                this.resetForm();
            });
        },
    }
});
